import { useState, useEffect, useRef, useCallback } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { ProjectMedia, PresignedUrlResponse } from "@/types";
import { fetchMediaPresignedUrl } from "@/services/api";
import { X, ImageOff, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSwipeable } from "react-swipeable";

interface ProjectMediaFullScreenSliderProps {
  projectId: string;
  media: ProjectMedia[];
  initialIndex: number;
  onClose: () => void;
}

const ProjectMediaFullScreenSlider = ({
  projectId,
  media,
  initialIndex,
  onClose,
}: ProjectMediaFullScreenSliderProps) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [presignedUrls, setPresignedUrls] = useState<
    Record<string, PresignedUrlResponse>
  >({});
  const [loadingUris, setLoadingUris] = useState<Record<string, boolean>>({});
  const observers = useRef<Record<string, IntersectionObserver>>({});
  const [api, setApi] = useState<any>(null);
  const { t } = useLanguage();

  const fetchPresignedUrl = useCallback(
    async (uri: string) => {
      if (presignedUrls[uri] || loadingUris[uri]) return;

      setLoadingUris((prev) => ({ ...prev, [uri]: true }));

      try {
        const result = await fetchMediaPresignedUrl(projectId, uri);
        if (result) {
          setPresignedUrls((prev) => ({ ...prev, [uri]: result }));
        }
      } catch (error) {
        console.error(`Error loading presigned URL for ${uri}:`, error);
      } finally {
        setLoadingUris((prev) => ({ ...prev, [uri]: false }));
      }
    },
    [projectId, presignedUrls, loadingUris],
  );

  useEffect(() => {
    Object.values(observers.current).forEach((observer) =>
      observer.disconnect(),
    );
    observers.current = {};

    media.forEach((item) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              fetchPresignedUrl(item.uri);
            }
          });
        },
        { threshold: 0.1 },
      );

      observers.current[item.uri] = observer;
    });

    return () => {
      Object.values(observers.current).forEach((observer) =>
        observer.disconnect(),
      );
    };
  }, [media, fetchPresignedUrl]);

  useEffect(() => {
    if (!media.length) return;

    const currentMedia = media[activeIndex];
    fetchPresignedUrl(currentMedia.uri);

    if (activeIndex > 0) {
      const prevMedia = media[activeIndex - 1];
      fetchPresignedUrl(prevMedia.uri);
    }

    if (activeIndex < media.length - 1) {
      const nextMedia = media[activeIndex + 1];
      fetchPresignedUrl(nextMedia.uri);
    }
  }, [activeIndex, media, fetchPresignedUrl]);

  const handlePrev = useCallback(() => {
    if (activeIndex > 0) {
      const newIndex = activeIndex - 1;
      setActiveIndex(newIndex);
      if (api) api.scrollTo(newIndex);
    }
  }, [activeIndex, api]);

  const handleNext = useCallback(() => {
    if (activeIndex < media.length - 1) {
      const newIndex = activeIndex + 1;
      setActiveIndex(newIndex);
      if (api) api.scrollTo(newIndex);
    }
  }, [activeIndex, media.length, api]);

  // Fixed keyboard navigation effect - separate from the api initialization effect
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeIndex, media.length, onClose, handlePrev, handleNext]);

  // Separate effect to handle API initialization
  useEffect(() => {
    if (api) {
      api.scrollTo(initialIndex, true);
    }
  }, [api, initialIndex]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrev(),
    preventScrollOnSwipe: true,
    trackMouse: false,
  });

  const imageCount = media.length;

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col h-screen"
      {...swipeHandlers}
    >
      <div className="flex justify-between items-center p-4 text-white">
        <div className="text-sm md:text-base">
          {t("common.imageCounter", {
            current: String(activeIndex + 1),
            total: String(imageCount),
          })}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-white hover:bg-white/20"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      <div
        className="flex-1 relative flex items-center justify-center"
        style={{ maxHeight: "calc(100vh - 120px)" }}
      >
        <Carousel
          setApi={setApi}
          className="w-full"
          opts={{
            align: "center",
            containScroll: false,
          }}
          onSelect={(idx) => {
            if (typeof idx === "number") {
              setActiveIndex(idx);
            }
          }}
        >
          <CarouselContent className="h-full">
            {media.map((item, index) => (
              <CarouselItem
                key={item.uri}
                className="h-full flex items-center justify-center"
                ref={(el) => {
                  if (el && observers.current[item.uri]) {
                    observers.current[item.uri].observe(el);
                  }
                }}
              >
                <div className="relative w-full h-full flex items-center justify-center">
                  {item.fileFormat === "mp4" ? (
                    <video
                      src={presignedUrls[item.uri]?.downloadUrl}
                      controls
                      autoPlay
                      className="max-w-full max-h-full object-contain"
                      style={{ maxHeight: "calc(100vh - 120px)" }}
                    />
                  ) : presignedUrls[item.uri] ? (
                    <img
                      src={presignedUrls[item.uri].downloadUrl}
                      alt={item.fileName}
                      className="max-w-full max-h-full object-contain"
                      style={{ maxHeight: "calc(100vh - 120px)" }}
                      loading={
                        Math.abs(index - activeIndex) <= 1 ? "eager" : "lazy"
                      }
                    />
                  ) : loadingUris[item.uri] ? (
                    <div className="flex flex-col items-center justify-center text-white">
                      <Spinner className="h-10 w-10 text-white" />
                      <p className="mt-4">{t("common.imageLoading")}</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-white">
                      <ImageOff className="h-16 w-16 text-white/60" />
                      <p className="mt-4">{t("common.imageLoadError")}</p>
                    </div>
                  )}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {activeIndex > 0 && (
          <Button
            variant="silvaconsultWhiteOutline"
            size="icon"
            onClick={handlePrev}
            className="absolute left-4 h-10 w-10 md:h-12 md:w-12 rounded-full"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
        )}

        {activeIndex < media.length - 1 && (
          <Button
            variant="silvaconsultWhiteOutline"
            size="icon"
            onClick={handleNext}
            className="absolute right-4 h-10 w-10 md:h-12 md:w-12 rounded-full"
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProjectMediaFullScreenSlider;
